import { useState } from "react";
import { getApiUrl } from "../../../../../config/api";

const BASE_PATH = "/api/v2/channel-visibility";

// ── Endpoint data (sourced from channel_product_visibility_routes.py) ─────────

const GROUPS = [
    {
        label: "Products",
        colorClass: "bg-blue-100 text-blue-700",
        endpoints: [
            {
                id: "list-products",
                method: "GET",
                path: "/by-api-key/products",
                summary: "List products",
                description:
                    "Retrieve all products visible to your channel. Results are paginated. Use query params to filter by language, category, compliance flags, or pinned status.",
                params: [
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                    { name: "page", type: "integer", required: false, description: "Page number (default: 1)" },
                    { name: "per_page", type: "integer", required: false, description: "Items per page, 1–200 (default: 20)" },
                    { name: "pinned_first", type: "boolean", required: false, description: "Sort pinned products to the top" },
                    { name: "pinned", type: "boolean", required: false, description: "Return only pinned products" },
                    { name: "category_id", type: "integer", required: false, description: "Filter by category ID" },
                    { name: "reach_compliant", type: "boolean", required: false, description: "Filter by REACH compliance" },
                    { name: "rohs_compliant", type: "boolean", required: false, description: "Filter by RoHS compliance" },
                    { name: "ul_comparable", type: "boolean", required: false, description: "Filter by UL comparable" },
                ],
                curlExtra: "?lang=en&page=1&per_page=20",
                response: `{
  "success": true,
  "products": [
    {
      "product_id": 12,
      "model_id": "TG-A4040",
      "category_id": 3,
      "description": "High-performance thermal interface material...",
      "pdf_url": "https://storage.googleapis.com/.../TG-A4040.pdf",
      "images": { "main": "https://...", "gallery": [] },
      "rohs_compliant": true,
      "reach_compliant": true,
      "ul_comparable": false,
      "is_pinned": false
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 20,
    "total": 97,
    "pages": 5
  }
}`,
            },
            {
                id: "get-product",
                method: "GET",
                path: "/by-api-key/products/:id",
                summary: "Get product by ID",
                description:
                    "Retrieve a single product by its numeric ID. Returns the channel-resolved version, including the pinned version if one is set.",
                pathParams: [
                    { name: ":id", type: "integer", required: true, description: "Product ID" },
                ],
                params: [
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                ],
                response: `{
  "success": true,
  "data": {
    "product_id": 12,
    "model_id": "TG-A4040",
    "category_id": 3,
    "description": "...",
    "pdf_url": "https://...",
    "images": { "main": "...", "gallery": [] },
    "rohs_compliant": true,
    "reach_compliant": true,
    "ul_comparable": false
  }
}`,
            },
            {
                id: "search-products",
                method: "GET",
                path: "/by-api-key/products/search",
                summary: "Search products",
                description:
                    "Full-text search across model IDs and descriptions. Optionally narrow by language and category.",
                params: [
                    { name: "q", type: "string", required: true, description: "Search keyword" },
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                    { name: "category_id", type: "integer", required: false, description: "Narrow results by category ID" },
                ],
                curlExtra: "?q=thermal+pad&lang=en",
                response: `{
  "success": true,
  "products": [ { "product_id": 12, "model_id": "TG-A4040", ... } ],
  "count": 5,
  "query": "thermal pad"
}`,
            },
            {
                id: "product-pdf",
                method: "GET",
                path: "/by-api-key/products/:id/pdf",
                summary: "Get product PDF URL",
                description:
                    "Returns just the PDF datasheet URL for the channel-resolved version of a product.",
                pathParams: [
                    { name: ":id", type: "integer", required: true, description: "Product ID" },
                ],
                params: [
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                ],
                response: `{
  "success": true,
  "product_id": 12,
  "pdf_url": "https://storage.googleapis.com/.../TG-A4040.pdf"
}`,
            },
            {
                id: "product-images",
                method: "GET",
                path: "/by-api-key/products/:id/images",
                summary: "Get product images",
                description:
                    "Returns the images object for the channel-resolved version of a product.",
                pathParams: [
                    { name: ":id", type: "integer", required: true, description: "Product ID" },
                ],
                params: [
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                ],
                response: `{
  "success": true,
  "product_id": 12,
  "images": {
    "main": "https://...",
    "gallery": ["https://...", "https://..."]
  }
}`,
            },
        ],
    },
    {
        label: "Categories",
        colorClass: "bg-purple-100 text-purple-700",
        endpoints: [
            {
                id: "list-categories",
                method: "GET",
                path: "/by-api-key/categories",
                summary: "List categories",
                description:
                    "Get all product categories available on your channel. Use the returned category_id values as filters in the products endpoint.",
                params: [],
                response: `{
  "success": true,
  "categories": [
    { "category_id": 1, "name": "Thermal Pads" },
    { "category_id": 2, "name": "Heat Sinks" }
  ],
  "count": 8
}`,
            },
        ],
    },
    {
        label: "Producers",
        colorClass: "bg-green-100 text-green-700",
        endpoints: [
            {
                id: "list-producers",
                method: "GET",
                path: "/by-api-key/producers",
                summary: "List producers",
                description:
                    "Get all producers (brands) whose products are visible on your channel.",
                params: [],
                response: `{
  "success": true,
  "producers": [
    { "producer_id": 1, "name": "T-Global Technology" }
  ],
  "count": 1
}`,
            },
            {
                id: "producer-products",
                method: "GET",
                path: "/by-api-key/producers/:id/products",
                summary: "Products by producer",
                description:
                    "Get all products belonging to a specific producer that are visible on your channel.",
                pathParams: [
                    { name: ":id", type: "integer", required: true, description: "Producer ID" },
                ],
                params: [
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                ],
                response: `{
  "success": true,
  "products": [ ... ],
  "count": 42,
  "producer_id": 1
}`,
            },
        ],
    },
    {
        label: "Channel",
        colorClass: "bg-orange-100 text-orange-700",
        endpoints: [
            {
                id: "channel-info",
                method: "GET",
                path: "/by-api-key/channel",
                summary: "Channel metadata",
                description:
                    "Get metadata about the channel authenticated by your API key — name, supported languages, and allowed origin settings.",
                params: [],
                response: `{
  "success": true,
  "data": {
    "channel_id": 3,
    "channel_name": "My Website",
    "allowed_languages": ["en", "zh"],
    "allowed_url": ["https://example.com"],
    "created_at": "2024-01-15T08:00:00Z"
  }
}`,
            },
            {
                id: "channel-products",
                method: "GET",
                path: "/:channel_id/products",
                summary: "Full catalogue by channel ID",
                description:
                    "Get the complete product catalogue visible on a channel with pinned versions applied. Use this when you know the channel ID directly.",
                pathParams: [
                    { name: ":channel_id", type: "integer", required: true, description: "Channel ID" },
                ],
                params: [
                    { name: "lang", type: "string", required: false, description: "Language code: en | zh | tw | jp" },
                ],
                response: `{
  "success": true,
  "data": [ { "product_id": 12, "model_id": "TG-A4040", ... } ],
  "count": 97
}`,
            },
            {
                id: "channel-pins",
                method: "GET",
                path: "/:channel_id/pins",
                summary: "Pinned version overrides",
                description:
                    "Get the explicit pinned version overrides for a channel. When a product has a pin, that specific version is served instead of the latest.",
                pathParams: [
                    { name: ":channel_id", type: "integer", required: true, description: "Channel ID" },
                ],
                params: [
                    { name: "lang", type: "string", required: false, description: "Filter pins by language code" },
                ],
                response: `{
  "success": true,
  "data": [
    {
      "product_id": 12,
      "version_id": 5,
      "lang_code": "en",
      "pinned_at": "2024-03-01T12:00:00Z"
    }
  ],
  "count": 3
}`,
            },
        ],
    },
];

const ERROR_CODES = [
    { code: "200", color: "text-green-600", description: "Request succeeded." },
    { code: "400", color: "text-yellow-600", description: "Missing or invalid query parameter (e.g. q is required for search)." },
    { code: "401", color: "text-red-500", description: "X-API-Key header is missing or the key is invalid." },
    { code: "403", color: "text-red-500", description: "Your origin is not in the channel's allowed_url list." },
    { code: "404", color: "text-orange-500", description: "The resource does not exist or is not visible to your channel." },
    { code: "429", color: "text-orange-500", description: "Rate limit exceeded. Slow down requests and retry after a moment." },
    { code: "500", color: "text-red-600", description: "Unexpected server error. Contact support if it persists." },
];

const METHOD_STYLE = {
    GET:    { badge: "bg-green-100 text-green-700 border border-green-200",  bar: "bg-green-500" },
    POST:   { badge: "bg-blue-100 text-blue-700 border border-blue-200",    bar: "bg-blue-500"  },
    DELETE: { badge: "bg-red-100 text-red-700 border border-red-200",       bar: "bg-red-500"   },
    PATCH:  { badge: "bg-yellow-100 text-yellow-700 border border-yellow-200", bar: "bg-yellow-500" },
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const handle = () => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button
            onClick={handle}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors"
        >
            {copied ? (
                <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                </>
            ) : (
                <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                </>
            )}
        </button>
    );
}

function CodeBlock({ code, color = "text-gray-300" }) {
    return (
        <div className="relative">
            <div className="absolute top-3 right-3">
                <CopyButton text={code} />
            </div>
            <pre className={`bg-gray-900 rounded-xl p-4 pr-20 text-sm ${color} overflow-x-auto leading-relaxed`}>
                {code}
            </pre>
        </div>
    );
}

function ParamTable({ title, params }) {
    if (!params || params.length === 0) return null;
    return (
        <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 w-40">Parameter</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 w-24">Type</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 w-24">Required</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Description</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {params.map((p) => (
                            <tr key={p.name} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3">
                                    <code className="text-xs font-mono font-semibold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                                        {p.name}
                                    </code>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 font-mono">{p.type}</td>
                                <td className="px-4 py-3">
                                    {p.required ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                                            required
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />
                                            optional
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-600 leading-relaxed">{p.description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EndpointSection({ endpoint, baseUrl }) {
    const style = METHOD_STYLE[endpoint.method] || METHOD_STYLE.GET;

    const displayPath = endpoint.path
        .replace(/:id\b/g, "{id}")
        .replace(/:channel_id/g, "{channel_id}")
        .replace(/:producer_id/g, "{producer_id}");

    const curlPath = endpoint.path
        .replace(/:id\b/g, "12")
        .replace(/:channel_id/g, "3")
        .replace(/:producer_id/g, "1");

    const curlUrl = `${baseUrl}${BASE_PATH}${curlPath}${endpoint.curlExtra || ""}`;
    const curl = `curl "${curlUrl}" \\\n  -H "X-API-Key: YOUR_API_KEY"`;

    const hasParams = (endpoint.pathParams?.length || 0) + (endpoint.params?.length || 0) > 0;

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Coloured left bar + endpoint title */}
            <div className={`flex items-center gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50`}>
                <div className={`w-1 h-10 rounded-full shrink-0 ${style.bar}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${style.badge}`}>
                            {endpoint.method}
                        </span>
                        <code className="text-sm font-mono font-semibold text-gray-800">
                            {displayPath}
                        </code>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 font-medium">{endpoint.summary}</p>
                </div>
            </div>

            <div className="p-5 space-y-6 bg-white">
                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed">{endpoint.description}</p>

                {/* Parameters */}
                {hasParams && (
                    <div className="space-y-4">
                        <ParamTable title="Path Parameters" params={endpoint.pathParams} />
                        <ParamTable title="Query Parameters" params={endpoint.params} />
                    </div>
                )}

                {/* Request example */}
                <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Request
                    </p>
                    <CodeBlock code={curl} color="text-gray-300" />
                </div>

                {/* Response */}
                {endpoint.response && (
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Response
                        </p>
                        <CodeBlock code={endpoint.response} color="text-green-400" />
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main export ────────────────────────────────────────────────────────────────

export default function DocumentationTab({ language }) {
    const baseUrl = getApiUrl();

    const authSnippet =
        `// Include this header in every request\nfetch(\`${baseUrl}${BASE_PATH}/by-api-key/products\`, {\n  headers: {\n    "X-API-Key": "YOUR_API_KEY"\n  }\n})`;

    const curlSnippet =
        `curl "${baseUrl}${BASE_PATH}/by-api-key/products" \\\n  -H "X-API-Key: YOUR_API_KEY"`;

    return (
        <div className="animate-slideUp space-y-8">

            {/* ── Base URL ───────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Base URL</p>
                <div className="flex items-center gap-3 bg-gray-900 rounded-xl px-4 py-3">
                    <code className="flex-1 text-sm text-orange-400 font-mono break-all">
                        {baseUrl}{BASE_PATH}
                    </code>
                    <CopyButton text={`${baseUrl}${BASE_PATH}`} />
                </div>
                <p className="mt-3 text-xs text-gray-500">
                    All endpoint paths below are appended to this base URL.
                    Every request must include the{" "}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-mono">X-API-Key</code>{" "}
                    header with your API key.
                </p>
            </div>

            {/* ── Authentication ─────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Authentication</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Pass your API key as a request header. It is never sent as a URL parameter.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-gray-400 font-medium mb-1.5">JavaScript / fetch</p>
                        <CodeBlock code={authSnippet} />
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 font-medium mb-1.5">curl</p>
                        <CodeBlock code={curlSnippet} />
                    </div>
                </div>

                <div className="flex items-start gap-2.5 bg-amber-50 rounded-xl p-3.5 border border-amber-200">
                    <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-xs text-amber-800 leading-relaxed">
                        <strong>Keep your API key private.</strong> Do not expose it in public frontend code
                        or commit it to a repository. If you believe it has been compromised, regenerate it
                        immediately from the Keys tab.
                    </p>
                </div>
            </div>

            {/* ── Endpoint groups ────────────────────────────────────────── */}
            {GROUPS.map((group) => (
                <div key={group.label}>
                    {/* Group heading */}
                    <div className="flex items-center gap-3 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${group.colorClass}`}>
                            {group.label}
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400">
                            {group.endpoints.length} endpoint{group.endpoints.length !== 1 ? "s" : ""}
                        </span>
                    </div>

                    {/* Endpoints — always fully visible */}
                    <div className="space-y-5">
                        {group.endpoints.map((ep) => (
                            <EndpointSection key={ep.id} endpoint={ep} baseUrl={baseUrl} />
                        ))}
                    </div>
                </div>
            ))}

            {/* ── Error codes ────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Error Codes</h3>
                <p className="text-xs text-gray-500 mb-4">
                    All error responses follow the same shape:{" "}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-gray-700">
                        {"{ \"success\": false, \"error\": \"reason\" }"}
                    </code>
                </p>
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 w-20">Code</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">What it means</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {ERROR_CODES.map((e) => (
                                <tr key={e.code} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <code className={`text-sm font-bold font-mono ${e.color}`}>{e.code}</code>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-600 leading-relaxed">{e.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
}
